// src/components/EcoPracticesPage.js
import React from 'react';
import { Helmet } from 'react-helmet-async';
import '../styles/tailwind.css';
import ecoIdentification from '../assets/eco-identification.jpg';
import ecoPracticesHero from '../assets/eco_practices.png';
import bclandscape from '../assets/bclandscape.jpg';
import bclandscapeWebp from '../assets/bclandscape.webp';
import ecoPrecision from '../assets/eco-precision.jpg';
import Section from './Section.js';
import AnimateOnScroll from './AnimateOnScroll.js';
import PageHero from './PageHero.js';

const EcoPracticesPage = () => {
    return (
        <div className='bg-custom-earth'>
            <div className="wood-container">
                <div className="min-h-screen flex flex-col">
                    <Helmet>
                        <title>IPM & Environmental Practices | Boultbee BC</title>
                        <meta name="description" content="Science-based herbicide application. IPM compliant practices that protect ecosystems while delivering results." />
                        <link rel="canonical" href="https://www.weedmanager.com/ecopractices" />
                        <meta property="og:type" content="website" />
                        <meta property="og:site_name" content="Boultbee Vegetation Management" />
                        <meta property="og:title" content="IPM & Environmental Practices | Boultbee BC" />
                        <meta property="og:description" content="Science-based herbicide application. IPM compliant practices that protect ecosystems while delivering results." />
                        <meta property="og:url" content="https://www.weedmanager.com/ecopractices" />
                        <meta property="og:image" content="https://www.weedmanager.com/images/og-eco.jpg" />
                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:title" content="IPM & Environmental Practices | Boultbee BC" />
                        <meta name="twitter:description" content="Science-based herbicide application. IPM compliant practices that protect ecosystems while delivering results." />
                        <meta name="twitter:image" content="https://www.weedmanager.com/images/og-eco.jpg" />
                        <script type="application/ld+json">
                          {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
                              { "@type": "ListItem", "position": 2, "name": "Eco Practices", "item": "https://www.weedmanager.com/ecopractices" }
                            ]
                          })}
                        </script>
                    </Helmet>

                    <PageHero
                      title="IPM & Environmental Practices"
                      subtitle="Science-based herbicide application that protects ecosystems while delivering results"
                      backgroundImage={ecoPracticesHero}
                    />

                    {/* Science-Based Application Section */}
                    <AnimateOnScroll animation="fade-in-right">
                    <Section
                        heading="Science-Based Application"
                        paragraph="Every Boultbee herbicide program begins with science. Precise application means less product, lower cost, and better results. We identify target species and assess site conditions, consider proximity to sensitive areas, select the most effective herbicide at the lowest effective rate, and evaluate plant lifecycle stage and environmental conditions before any application."
                        imgSrc={ecoIdentification}
                        altText="Science-based weed identification for targeted herbicide application"
                        imgWidth={1200}
                        imgHeight={630}
                    />
                    </AnimateOnScroll>

                    {/* IPM Compliance Section */}
                    <AnimateOnScroll animation="fade-in-left">
                    <Section
                        heading="IPM Compliance as Standard Practice"
                        paragraph="Integrated Pest Management is our standard operating method and has been since before BC legislation mandated it. IPM means using prevention, monitoring, and targeted intervention before resorting to broad-spectrum treatment. We consider the plant's lifecycle stage, environmental conditions, and susceptible modes of action when forming treatment plans. We rotate herbicides to prevent resistance development and continuously research lower-impact products entering the market. The result is measurably effective vegetation control that satisfies both operational requirements and environmental regulatory standards."
                        imgSrc={bclandscape}
                        webpSrc={bclandscapeWebp}
                        altText="British Columbia landscape representing environmental stewardship in vegetation management"
                        imgWidth={715}
                        imgHeight={553}
                    />
                    </AnimateOnScroll>

                    {/* Technology Reduces Impact Section */}
                    <AnimateOnScroll animation="fade-in-right">
                    <Section
                        heading="Technology Reduces Environmental Impact"
                        paragraph="GPS-tracked, flow-controlled application systems reduce overlaps and minimize over-application. Variable analysis ensures the right product is used for the right species. We also use in-house technologies to determine optimal timing and help inform treatment limits and product selection. The result: less herbicide in the environment with better vegetation control outcomes."
                        imgSrc={ecoPrecision}
                        altText="GPS precision technology showing targeted spray coverage boundaries"
                        imgWidth={1200}
                        imgHeight={630}
                    />
                    </AnimateOnScroll>

                </div>
            </div>
        </div>
    );
};

export default EcoPracticesPage;
